var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { Icon, MapBox } from '@homeexchange/design';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import i18n from '../../../i18n';
import { useSelector, useDispatch } from 'react-redux';
import { toggleFavorite } from '../../../actions/homeFavorites';
import styles from './searchMap.module.scss';
import { useDevice } from '@homeexchange/shared';
import { Legends } from './legends';
import clsx from 'clsx';
import { HomeCard } from '../results-ts/homecard';
export var SearchMap = React.forwardRef(function (_a, ref) {
    var _b;
    var _c, _d;
    var results = _a.results, additionalResults = _a.additionalResults, initialBounds = _a.initialBounds, onBoundsChange = _a.onBoundsChange, loading = _a.loading, hoveredHomeId = _a.hoveredHomeId, user = _a.user, isLogged = _a.isLogged, searchFilters = _a.searchFilters;
    var local = i18n.language;
    var mapRef = useRef(null);
    var popupRef = useRef(null);
    var isProgrammaticChange = useRef(false);
    var _e = useState(null), hoveredMarkerId = _e[0], setHoveredMarkerId = _e[1];
    var homeFavorites = useSelector(function (state) { return state.homeFavorites || []; });
    var _f = useState(null), selectedHomeId = _f[0], setSelectedHomeId = _f[1];
    var markerRefs = useRef(new Map());
    var _g = useState(false), isClosing = _g[0], setIsClosing = _g[1];
    var _h = useState(0), animationKey = _h[0], setAnimationKey = _h[1];
    var isMobile = useDevice().isMobile;
    var ANIMATION_DURATION = 200;
    var dispatch = useDispatch();
    var handleToggleFavorite = useCallback(function (homeId) {
        dispatch(toggleFavorite(homeId, homeFavorites));
        if (homeFavorites.includes(homeId)) {
            dispatch({
                type: 'REMOVE_HOME_FAVORITE',
                homeFavorites: homeId
            });
        }
        else {
            dispatch({
                type: 'ADD_HOME_FAVORITE',
                homeFavorites: homeId
            });
        }
    }, [dispatch, homeFavorites]);
    useEffect(function () {
        setHoveredMarkerId(hoveredHomeId);
    }, [hoveredHomeId]);
    var handleClosePopup = function () {
        setIsClosing(true);
        if (isMobile) {
            setTimeout(function () {
                setSelectedHomeId(null);
                setIsClosing(false);
            }, ANIMATION_DURATION);
        }
        else {
            setSelectedHomeId(null);
            setIsClosing(false);
        }
    };
    var handleMarkerClick = function (homeId) {
        if (selectedHomeId) {
            if (selectedHomeId !== homeId) {
                setIsClosing(true);
                if (isMobile) {
                    setTimeout(function () {
                        setSelectedHomeId(homeId);
                        setIsClosing(false);
                        setAnimationKey(function (prev) { return prev + 1; });
                    }, ANIMATION_DURATION);
                }
                else {
                    setSelectedHomeId(homeId);
                    setIsClosing(false);
                }
            }
            else {
                handleClosePopup();
            }
        }
        else {
            setSelectedHomeId(homeId);
            setAnimationKey(function (prev) { return prev + 1; });
        }
    };
    useEffect(function () {
        var handleOutsideClick = function (e) {
            if (popupRef.current && popupRef.current.contains(e.target)) {
                return;
            }
            var clickedOnMarker = false;
            markerRefs.current.forEach(function (markerEl) {
                if (markerEl.contains(e.target)) {
                    clickedOnMarker = true;
                }
            });
            if (!clickedOnMarker && selectedHomeId) {
                handleClosePopup();
            }
        };
        document.addEventListener('click', handleOutsideClick);
        return function () {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [selectedHomeId]);
    var createMarkers = function (results, isUnavailable) {
        var _a;
        if (isUnavailable === void 0) { isUnavailable = false; }
        return ((_a = results === null || results === void 0 ? void 0 : results.map(function (result) {
            var _a;
            var homeId = result.attributes.homeId;
            var isFavorite = homeFavorites.includes(homeId);
            var isHovered = hoveredMarkerId === homeId;
            var isSelected = selectedHomeId === homeId;
            var iconName = isUnavailable ? 'marker-home-unavailable' : 'marker-home';
            if (isHovered || isSelected) {
                iconName = isFavorite ? 'marker-home-selected-favorite' : 'marker-home-selected';
            }
            else if (isFavorite) {
                iconName = isUnavailable
                    ? 'marker-home-unavailable-favorite'
                    : 'marker-home-favorite';
            }
            var getPositionOrder = function () {
                switch (true) {
                    case isHovered || isSelected:
                        return 10;
                    case isUnavailable && !isFavorite:
                        return 1;
                    case isUnavailable && isFavorite:
                        return 2;
                    case !isUnavailable && !isFavorite:
                        return 3;
                    case !isUnavailable && isFavorite:
                        return 4;
                    default:
                        return -1;
                }
            };
            return {
                latitude: result.attributes.location.coordinates.latitude,
                longitude: result.attributes.location.coordinates.longitude,
                indexPosition: getPositionOrder(),
                content: (React.createElement("div", { ref: function (el) {
                        if (el)
                            markerRefs.current.set(homeId, el);
                        else
                            markerRefs.current.delete(homeId);
                    }, className: clsx(styles['marker-container'], (_a = {},
                        _a[styles['marker-hovered']] = isHovered || isSelected,
                        _a)), onMouseEnter: function () { return setHoveredMarkerId(homeId); }, onMouseLeave: function () { return setHoveredMarkerId(null); }, onClick: function () { return handleMarkerClick(homeId); } },
                    React.createElement(Icon, { name: iconName, size: "4xl" })))
            };
        })) !== null && _a !== void 0 ? _a : []);
    };
    var markers = createMarkers(results);
    var additionalMarkers = createMarkers(additionalResults, true);
    var handleBoundsChange = useCallback(function (newBounds) {
        if (!isProgrammaticChange.current && onBoundsChange) {
            onBoundsChange(newBounds);
        }
        else {
            isProgrammaticChange.current = false;
        }
    }, [onBoundsChange]);
    var handleMoveEnd = useCallback(function (_event, bounds) {
        if (bounds) {
            var convertedBounds = {
                ne: { lat: bounds._ne.lat, lon: bounds._ne.lng },
                sw: { lat: bounds._sw.lat, lon: bounds._sw.lng }
            };
            handleBoundsChange(convertedBounds);
        }
    }, [handleBoundsChange]);
    useImperativeHandle(ref, function () { return ({
        unzoom: function (scale) {
            if (scale === void 0) { scale = 1.5; }
            if (mapRef.current) {
                var mapInstance = mapRef.current;
                if (mapInstance.getZoom && mapInstance.zoomTo) {
                    var currentZoom = mapInstance.getZoom();
                    mapInstance.zoomTo(currentZoom / scale);
                }
            }
        },
        flyTo: function (bounds) {
            if (mapRef.current) {
                var mapInstance = mapRef.current;
                if (mapInstance.fitBounds) {
                    isProgrammaticChange.current = true;
                    var mapBounds = [
                        [bounds.sw.lon, bounds.sw.lat],
                        [bounds.ne.lon, bounds.ne.lat]
                    ];
                    mapInstance.fitBounds(mapBounds, {
                        duration: 3000,
                        animate: true
                    });
                }
            }
        }
    }); });
    return (React.createElement(React.Fragment, null,
        loading && (React.createElement("div", { className: styles['loading-container'] },
            React.createElement("div", { className: styles['loading-dot'] }),
            React.createElement("div", { className: styles['loading-dot'] }),
            React.createElement("div", { className: styles['loading-dot'] }))),
        React.createElement(Legends, null),
        React.createElement(MapBox, __assign({ ref: mapRef, mapStyle: "https://api.jawg.io/styles/jawg-sunny.json?access-token=ISSuiu5R48VFKdltBfogTgBSdaH68izqpiQ9C0WkBXS2Jg1lQSp02Hy5vVPCTgfT&lang=".concat(local), positionNavControl: "top-right", className: styles['search-map'], markers: __spreadArray(__spreadArray([], additionalMarkers, true), markers, true), bounds: initialBounds && [
                initialBounds.sw.lon,
                initialBounds.sw.lat,
                initialBounds.ne.lon,
                initialBounds.ne.lat
            ], onMoveEnd: handleMoveEnd, debounceEventsTime: 600 }, (selectedHomeId &&
            __spreadArray(__spreadArray([], results, true), additionalResults, true).find(function (result) { return result.attributes.homeId === selectedHomeId; }) && {
            popup: {
                longitude: (_c = __spreadArray(__spreadArray([], results, true), additionalResults, true).find(function (result) { return result.attributes.homeId === selectedHomeId; })) === null || _c === void 0 ? void 0 : _c.attributes.location.coordinates.longitude,
                latitude: (_d = __spreadArray(__spreadArray([], results, true), additionalResults, true).find(function (result) { return result.attributes.homeId === selectedHomeId; })) === null || _d === void 0 ? void 0 : _d.attributes.location.coordinates.latitude,
                className: clsx(styles['map-popup'], (_b = {},
                    _b[styles['animate']] = selectedHomeId && !isClosing,
                    _b[styles['closing']] = isClosing,
                    _b)),
                content: (React.createElement(HomeCard, { key: "".concat(selectedHomeId, "-").concat(animationKey), result: __spreadArray(__spreadArray([], results, true), additionalResults, true).find(function (result) { return result.attributes.homeId === selectedHomeId; }), locale: local, isLogged: isLogged, isUserCollection: user === null || user === void 0 ? void 0 : user.get('is_he_collection'), homeFavorites: homeFavorites, handleToggleFavorite: handleToggleFavorite, format: "map", onClose: handleClosePopup, user: user, searchFilters: searchFilters, hideFixedDates: results.some(function (result) { return result.attributes.homeId === selectedHomeId; }), trackingEvent: "home_card", origin: results.find(function (result) { return result.attributes.homeId === selectedHomeId; })
                        ? 'main_search'
                        : 'dual_search' }))
            }
        })))));
});
